import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import {
  Platform, StyleSheet, Text, TextInput, Pressable, View, FlexStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Modal from 'react-native-modal';
import { CameraView, useCameraPermissions } from 'expo-camera';
import Colors from '../constants/Colors';
import Style from '../constants/Style';
import ScanIcon16 from './16pxIcons/ScanIcon16';
import CloseModalIcon from './CloseModalIcon';
import PasswordShownIcon from './24pxIcons/PasswordShownIcon';
import PasswordHiddenIcon from './24pxIcons/PasswordHiddenIcon';
import isMobile from './IsMobile';
import { FIELD_REQUIRED_MESSAGE } from '../constants/AlertMessages';
import PressableStyled from './PressableStyled';
import CopyIcon from './24pxIcons/CopyIcon';
import titleCase from './TitleCase';

interface CustomTextFieldProps {
  title?: string,
  value: string,
  updateValue?: Dispatch<SetStateAction<string>>,
  editable?: boolean,
  required?: boolean,
  scanner?: boolean,
  password?: boolean,
  errorText?: string,
  invalid?: boolean,
  placeholder?: string,
  testID?: string,
  maxLength?: number,
  disableAuto?: boolean,
  multiline?: boolean,
  indicateLength?: boolean,
  copy?: () => void,
  style?: FlexStyle | FlexStyle[],
  ref?,
  onFocus?: () => void,
}

export default function CustomTextField({
  title, value, updateValue, editable = true, required = false, errorText, placeholder, testID,
  scanner = false, password = false, invalid = false, maxLength, disableAuto = false, multiline,
  indicateLength, copy, style, onFocus, ref,
}: CustomTextFieldProps) {
  const [secure, setSecure] = useState<boolean>(password);
  const [focused, setFocused] = useState<boolean>(false);
  const [scannerOpen, setScannerOpen] = useState<boolean>(false);
  const [fieldSelection,
    setFieldSelection] = useState<{ start: number, end?: number }>({ start: 0 });
  const [status, requestPermission] = useCameraPermissions();

  function getBorderColor() {
    if (invalid) return Colors.light.hereworksRed500;
    return focused ? Colors.light.hereworksBlue400 : Colors.light.hereworksBlue100;
  }

  const showScanner = () => {
    if (!status.granted && status.canAskAgain) return <Text>{'Requesting for camera permission'}</Text>;
    if (!status.granted) return <Text>{'No access to camera'}</Text>;

    return (
      <CameraView
        onBarcodeScanned={scannerOpen ? handleBarCodeScanned : undefined}
        style={StyleSheet.absoluteFillObject}
      />
    );
  };

  const handleBarCodeScanned = ({ data }) => {
    setScannerOpen(false);
    updateValue(data);
  };

  useEffect(() => {
    if (isMobile && scanner && scannerOpen && !status.granted) {
      requestPermission();
    }
  }, [scannerOpen]);

  const topSafeAreaInset = useSafeAreaInsets().top;

  const renderPasswordIcon = () => (secure
    ? <View style={{ backgroundColor: Colors.light.hereworksWhite200 }}>
      <PasswordShownIcon /></View> : <PasswordHiddenIcon />);

  return (
    <>
      {title && <Text style={styles.heading}>
        {`${title}${required ? ' *' : ''}`}
      </Text>}
      <View style={styles.textContainer}>
        <TextInput
          {...{
            editable, placeholder, testID, onFocus, ref,
          }}
          placeholderTextColor={Colors.light.hereworksBlack400}
          selection={fieldSelection}
          onSelectionChange={({ nativeEvent: { selection } }) => {
            setFieldSelection(selection);
          }}
          secureTextEntry={secure}
          style={[
            styles.inputText,
            copy && styles.inputInset,
            {
              borderColor: getBorderColor(),
              color:
                editable ?? true ? Colors.light.hereworksBlack600 : Colors.light.hereworksBlack100,
            },
            style,
          ]}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          value={value ?? ''}
          onChangeText={updateValue}
          autoCorrect={!disableAuto}
          autoCapitalize={disableAuto ? 'none' : 'sentences'}
          {...{ maxLength, multiline }}
        />
        {copy && <PressableStyled
          style={styles.copyButton}
          onPress={copy}
        >
          <CopyIcon />
        </PressableStyled>}
        {isMobile && scanner && (
          <>
            <Pressable
              onPress={() => setScannerOpen(true)}
              style={(state: { hovered: boolean, pressed: boolean }) => [
                styles.detailedFilterButton,
                state.hovered && Style.hovered, state.pressed && Style.pressed,
              ]}
            >
              <ScanIcon16 />
            </Pressable>
            {scannerOpen && (
              <Modal
                isVisible={scannerOpen}
                style={{ alignItems: 'center', margin: 0 }}
                onSwipeComplete={() => setScannerOpen(false)}
                swipeDirection={'down'}
                animationIn={'fadeIn'}
                animationOut={'fadeOut'}
              >
                {showScanner()}
                <Pressable
                  onPress={() => setScannerOpen(false)}
                  style={(state: { hovered: boolean, pressed: boolean }) => [
                    Style.closeButton, { top: topSafeAreaInset ?? 32 },
                    state.hovered && Style.hovered, state.pressed && Style.pressed,
                  ]}
                >
                  <CloseModalIcon />
                </Pressable>
              </Modal>
            )}
          </>
        )}
        {password && <Pressable
          style={(state: { hovered: boolean, pressed: boolean }) => [
            styles.passwordVisibilityToggle,
            state.hovered && Style.hovered, state.pressed && Style.pressed,
          ]}
          onPress={() => setSecure(!secure)}
        >
          {renderPasswordIcon()}
        </Pressable>}
      </View>
      <View style={Style.row}>
        {invalid && (
          <Text style={styles.errorText}>
            {titleCase(errorText ?? FIELD_REQUIRED_MESSAGE)}
          </Text>
        )}
        {indicateLength && maxLength && <Text style={styles.lengthIndicator}>
          {`${value.length}/${maxLength}`}
        </Text>}
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  textContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  heading: {
    fontSize: 16,
    lineHeight: 24,
    marginTop: 12,
    marginBottom: 4,
    alignSelf: 'flex-start',
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  inputText: {
    flex: 1,
    color: Colors.light.hereworksBlack600,
    alignSelf: 'stretch',
    fontFamily: 'Poppins_400Regular',
    alignItems: 'center',
    fontSize: 14,
    lineHeight: Platform.OS === 'ios' ? undefined : 21,
    alignContent: 'center',
    backgroundColor: '#FFF',
    borderColor: Colors.light.hereworksBlue100,
    paddingHorizontal: 8,
    paddingVertical: 10,
    borderRadius: 5,
    borderWidth: 1,
    minWidth: 1,
    outlineWidth: 0,
  },
  inputInset: {
    paddingEnd: 40,
  },
  errorText: {
    color: Colors.light.hereworksRed500,
    fontFamily: 'Roboto_400Regular',
    marginTop: 4,
    fontSize: 12,
    lineHeight: 20,
    alignSelf: 'flex-start',
  },
  detailedFilterButton: {
    backgroundColor: '#fff',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    shadowOffset: { width: 0, height: 2 },
    shadowColor: '#323247',
    marginStart: 8,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    aspectRatio: 1,
    padding: 14,
  },
  passwordVisibilityToggle: {
    position: 'absolute',
    end: 0,
    padding: 6,
  },
  lengthIndicator: {
    fontSize: 12,
    lineHeight: 20,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack300,
  },
  copyButton: {
    position: 'absolute',
    end: 8,
  },
});
